import { useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router'
import { useAiAgent } from '../../../../utils/contexts/ai/AiAgentContext'
import { useCoreContext } from '../../../../utils/core/RPPCoreContext'
import {
  type NavMenuHierarchy,
  type NavMenuHierarchyL1,
  type NavMenuHierarchyL2,
} from '../NavMenuHierarchy'
import { useGoToPathDynamicFactory } from '../../../../utils/core/factories/routing/routingHooks.factories'
import { useAccessControl } from '../../../../exports/access-control'

type RouteDescription = {
  route: string
  description: string
}

export const useNavigationAi = (navMenuHierarchy: NavMenuHierarchy) => {
  const { registerAiFunction, removeAiFunction, registerAiData, removeAiData } = useAiAgent()
  const { routesByName } = useCoreContext()
  const hasAccess = useAccessControl()
  const paramsRef = useRef<ReturnType<typeof useParams>>(useParams())
  const currentParams = paramsRef.current
  const navigate = useGoToPathDynamicFactory()
  const routeDescriptions = useMemo(() => {
    // Recursively collect all leaf navigation entries (those with routes)
    const routeDescriptions: RouteDescription[] = []

    const traverseNode = (node: NavMenuHierarchyL1 | NavMenuHierarchyL2) => {
      // If this node has a route, it's a leaf node
      if ('route' in node && 'llmDescription' in node) {
        // If the node does not have an access control, or the user has access to it, add the route description
        if (!node.accessControl || hasAccess(node.accessControl)) {
          // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
          routeDescriptions.push({ route: node.route, description: node.llmDescription })
        }
      }

      if ('children' in node && node.children && node.children.length > 0) {
        // Recursively traverse children
        node.children.forEach(child => traverseNode(child))
      }
    }

    // Start traversal from the hierarchy root
    navMenuHierarchy.hierarchy.forEach(l1 => traverseNode(l1))

    return routeDescriptions
  }, [navMenuHierarchy, hasAccess])

  // Register AI function for navigation
  useEffect(() => {
    registerAiData({
      key: 'appRouteDescriptions',
      description: 'The descriptions of the routes in the application',
      data: routeDescriptions.map(r => `${r.route}: ${r.description}`).join('\n'),
    })

    registerAiFunction({
      key: 'navigateToRoute',
      description: 'Navigate to a specific route in the application using the route name',
      parameters: {
        type: 'object',
        properties: {
          route: {
            type: 'string',
            description: 'The route name to navigate to (e.g., "dashboard", "catalog", "reports")',
          },
        },
        required: ['route'],
      },
      fn: (route: unknown) => {
        const routeName = route as string

        try {
          // First try exact match
          if (routesByName[routeName]) {
            navigate(routeName, currentParams)
            return { success: true, result: `Successfully navigated to route: ${routeName}` }
          }

          // No match found
          const availableRoutes = Object.keys(routesByName)
          const errorMessage = `Route not found: ${routeName}. Available routes: ${availableRoutes.join(
            ', '
          )}`
          return {
            success: false,
            result: errorMessage,
          }
        } catch (error) {
          console.error('Error navigating to route:', error)
          return {
            success: false,
            result: `Failed to navigate to route: ${routeName}. Error: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          }
        }
      },
    })

    return () => {
      removeAiData('appRouteDescriptions')
      removeAiFunction('navigateToRoute')
    }
  }, [
    registerAiFunction,
    removeAiFunction,
    routesByName,
    currentParams,
    navigate,
    registerAiData,
    removeAiData,
    routeDescriptions,
  ])
}
